import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatPaginator } from '@angular/material/paginator';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { WorkLogService, WorkLog, WorkLogStats, WorkLogTimeline, CreateWorkLog, UpdateWorkLog, resolveWorkLogImageUrl, WorkLogImageGeo } from '../shared/services/work-log.service';
import { ApiService } from '../shared/services/api.service';

interface PendingAttachment {
  file: File;
  preview: string;
  latitude?: number;
  longitude?: number;
  fromCamera: boolean;
}

@Component({
  selector: 'app-work-log',
  templateUrl: './work-log.component.html',
  styleUrls: ['./work-log.component.css']
})
export class WorkLogComponent implements OnInit, OnDestroy {
  @ViewChild('fileInput') fileInput!: ElementRef;
  @ViewChild('cameraInput') cameraInput!: ElementRef;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  private destroy$ = new Subject<void>();

  // Data properties
  workLogs: WorkLog[] = [];
  workLogStats: WorkLogStats | null = null;
  workLogTimeline: WorkLogTimeline[] = [];
  selectedWorkLog: WorkLog | null = null;
  projects: any[] = [];
  
  // Loading states
  loading = false;
  loadingStats = false;
  loadingLogs = false;
  uploadingImages = false;
  
  // Pagination
  currentPage = 1;
  totalPages = 1;
  totalItems = 0;
  itemsPerPage = 10;
  
  // Filters
  statusFilter = '';
  priorityFilter = '';
  projectFilter = '';
  searchTerm = '';
  startDate = '';
  endDate = '';
  
  // Forms
  workLogForm: FormGroup;
  commentForm: FormGroup;
  
  // Dialog states
  showCreateDialog = false;
  showEditDialog = false;
  showViewDialog = false;
  showImageDialog = false;
  
  // File upload
  selectedFiles: File[] = [];
  imagePreviewUrls: string[] = [];
  createAttachments: PendingAttachment[] = [];
  capturingGeo = false;
  private pendingGeo: { latitude: number; longitude: number } | null = null;
  
  // Current user
  currentUserId: number;

  constructor(
    private workLogService: WorkLogService,
    private apiService: ApiService,
    private formBuilder: FormBuilder,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {
    this.currentUserId = this.getCurrentUserId();
    this.initializeForms();
  }

  ngOnInit(): void {
    this.loadWorkLogStats();
    this.loadWorkLogs();
    this.loadProjects();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForms(): void {
    this.workLogForm = this.formBuilder.group({
      projectId: ['', Validators.required],
      logDate: ['', Validators.required],
      title: ['', Validators.required],
      description: ['', Validators.required],
      hoursWorked: [0, [Validators.min(0), Validators.max(24)]],
      progressPercentage: [0, [Validators.min(0), Validators.max(100)]],
      status: ['in_progress', Validators.required],
      priority: ['medium', Validators.required],
      tags: this.formBuilder.array([]),
      weatherConditions: [''],
      siteConditions: [''],
      safetyIncidents: [''],
      qualityIssues: [''],
      nextDayPlan: [''],
      challengesFaced: [''],
      solutionsImplemented: [''],
      materialsUsed: [''],
      equipmentUsed: [''],
      subcontractorsInvolved: ['']
    });

    this.commentForm = this.formBuilder.group({
      comment: ['', Validators.required]
    });
  }

  private getCurrentUserId(): number {
    const user = localStorage.getItem('user');
    if (user) {
      const userData = JSON.parse(user);
      return userData.user.id;
    }
    return 0;
  }

  // Load work log statistics
  loadWorkLogStats(): void {
    this.loadingStats = true;
    const params: any = {};
    
    if (this.projectFilter) params.projectId = this.projectFilter;
    if (this.startDate) params.startDate = this.startDate;
    if (this.endDate) params.endDate = this.endDate;

    this.workLogService.getWorkLogStats(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res:any) => {
          this.workLogStats = res.data;
          this.loadingStats = false;
        },
        error: (error) => {
          console.error('Error loading work log stats:', error);
          this.loadingStats = false;
          this.showNotification('Failed to load work log statistics', 'error');
        }
      });
  }

  // Load work logs
  loadWorkLogs(): void {
    this.loadingLogs = true;
    const params: any = {
      page: this.currentPage,
      limit: this.itemsPerPage
    };

    if (this.projectFilter) params.projectId = this.projectFilter;
    if (this.statusFilter) params.status = this.statusFilter;
    if (this.startDate) params.startDate = this.startDate;
    if (this.endDate) params.endDate = this.endDate;
    if (this.searchTerm) params.search = this.searchTerm;

    this.workLogService.getWorkLogs(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.workLogs = response.data;
          this.currentPage = response.pagination.current_page;
          this.totalPages = response.pagination.total_pages;
          this.totalItems = response.pagination.total_items;
          this.itemsPerPage = response.pagination.items_per_page;
          this.loadingLogs = false;
        },
        error: (error) => {
          console.error('Error loading work logs:', error);
          this.loadingLogs = false;
          this.showNotification('Failed to load work logs', 'error');
        }
      });
  }

  // Load projects (you'll need to implement this based on your project service)
  loadProjects(): void {
    this.apiService.getProjectListForDropdown({ limit: 200 }).subscribe({
      next: (res: any) => {
        const rows = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        this.projects = rows.map((p: any) => ({
          id: p.id ?? p.project_id,
          name: p.project_name ?? p.label ?? p.name ?? p.project_identifier ?? `Project ${p.id ?? p.project_id}`,
        }));
      },
      error: () => {
        this.projects = [];
      },
    });
  }

  // Load single work log
  loadWorkLog(id: number): void {
    console.log("id",id);
    this.workLogService.getWorkLog(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (workLog) => {
          this.selectedWorkLog = workLog;
        },
        error: (error) => {
          console.error('Error loading work log:', error);
          this.showNotification('Failed to load work log details', 'error');
        }
      });
  }

  // Open create dialog
  openCreateDialog(): void {
    this.workLogForm.reset();
    this.createAttachments = [];
    this.pendingGeo = null;
    this.workLogForm.patchValue({
      logDate: new Date().toISOString().split('T')[0],
      status: 'in_progress',
      priority: 'medium',
      hoursWorked: 0,
      progressPercentage: 0
    });
    this.showCreateDialog = true;
  }

  openCreateGalleryPicker(): void {
    this.fileInput?.nativeElement?.click();
  }

  async openCreateCameraCapture(): Promise<void> {
    this.capturingGeo = true;
    try {
      const position = await this.getCurrentPosition();
      if (!position) {
        this.showNotification('Enable GPS and allow location access for geo-tagged photos', 'error');
        return;
      }
      this.pendingGeo = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      };
      this.cameraInput?.nativeElement?.click();
    } finally {
      this.capturingGeo = false;
    }
  }

  onCreateGallerySelected(event: any): void {
    const files: FileList | null = event.target.files;
    if (!files?.length) return;
    Array.from(files).forEach((file) => {
      if (file.type.startsWith('image/')) {
        this.addCreateAttachment(file, false);
      }
    });
    event.target.value = '';
  }

  onCreateCameraSelected(event: any): void {
    const file: File | undefined = event.target.files?.[0];
    if (!file) {
      this.pendingGeo = null;
      return;
    }
    this.addCreateAttachment(
      file,
      true,
      this.pendingGeo?.latitude,
      this.pendingGeo?.longitude
    );
    this.pendingGeo = null;
    event.target.value = '';
  }

  private addCreateAttachment(
    file: File,
    fromCamera: boolean,
    latitude?: number,
    longitude?: number
  ): void {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.createAttachments.push({
        file,
        preview: e.target.result,
        latitude,
        longitude,
        fromCamera,
      });
    };
    reader.readAsDataURL(file);
  }

  removeCreateAttachment(index: number): void {
    this.createAttachments.splice(index, 1);
  }

  formatCoords(lat?: number | null, lng?: number | null): string {
    if (lat == null || lng == null) return '';
    return `${Number(lat).toFixed(5)}, ${Number(lng).toFixed(5)}`;
  }

  // Create work log
  createWorkLog(): void {
    if (this.workLogForm.valid) {
      const formData = this.workLogForm.value;
      
      const workLog: CreateWorkLog = {
        projectId: formData.projectId,
        logDate: formData.logDate,
        title: formData.title,
        description: formData.description,
        hoursWorked: formData.hoursWorked,
        progressPercentage: formData.progressPercentage,
        status: formData.status,
        priority: formData.priority,
        tags: formData.tags,
        weatherConditions: formData.weatherConditions,
        siteConditions: formData.siteConditions,
        safetyIncidents: formData.safetyIncidents,
        qualityIssues: formData.qualityIssues,
        nextDayPlan: formData.nextDayPlan,
        challengesFaced: formData.challengesFaced,
        solutionsImplemented: formData.solutionsImplemented,
        materialsUsed: formData.materialsUsed,
        equipmentUsed: formData.equipmentUsed,
        subcontractorsInvolved: formData.subcontractorsInvolved
      };

      this.workLogService.createWorkLog(workLog)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            const workLogId = response?.data?.id;
            if (workLogId && this.createAttachments.length > 0) {
              const files = this.createAttachments.map((a) => a.file);
              const imageGeo: WorkLogImageGeo[] = this.createAttachments.map((a) => ({
                ...(a.latitude != null ? { latitude: a.latitude } : {}),
                ...(a.longitude != null ? { longitude: a.longitude } : {}),
              }));
              this.workLogService.uploadImages(workLogId, files, { imageGeo })
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                  next: () => this.finishCreateSuccess(),
                  error: () => {
                    this.showNotification('Work log saved but some photos failed to upload', 'error');
                    this.finishCreateSuccess();
                  },
                });
            } else {
              this.finishCreateSuccess();
            }
          },
          error: (error) => {
            console.error('Error creating work log:', error);
            this.showNotification('Failed to create work log', 'error');
          }
        });
    }
  }

  // Open edit dialog
  openEditDialog(workLog: WorkLog): void {
    this.selectedWorkLog = workLog;
    this.workLogForm.patchValue({
      projectId: workLog.project_id,
      logDate: workLog.log_date,
      title: workLog.title,
      description: workLog.description,
      hoursWorked: workLog.hours_worked,
      progressPercentage: workLog.progress_percentage,
      status: workLog.status,
      priority: workLog.priority,
      tags: workLog.tags,
      weatherConditions: workLog.weather_conditions,
      siteConditions: workLog.site_conditions,
      safetyIncidents: workLog.safety_incidents,
      qualityIssues: workLog.quality_issues,
      nextDayPlan: workLog.next_day_plan,
      challengesFaced: workLog.challenges_faced,
      solutionsImplemented: workLog.solutions_implemented,
      materialsUsed: workLog.materials_used,
      equipmentUsed: workLog.equipment_used,
      subcontractorsInvolved: workLog.subcontractors_involved
    });
    this.showEditDialog = true;
  }

  // Update work log
  updateWorkLog(): void {
    if (this.workLogForm.valid && this.selectedWorkLog) {
      const formData = this.workLogForm.value;
      
      const workLog: UpdateWorkLog = {
        title: formData.title,
        description: formData.description,
        hoursWorked: formData.hoursWorked,
        progressPercentage: formData.progressPercentage,
        status: formData.status,
        priority: formData.priority,
        tags: formData.tags,
        weatherConditions: formData.weatherConditions,
        siteConditions: formData.siteConditions,
        safetyIncidents: formData.safetyIncidents,
        qualityIssues: formData.qualityIssues,
        nextDayPlan: formData.nextDayPlan,
        challengesFaced: formData.challengesFaced,
        solutionsImplemented: formData.solutionsImplemented,
        materialsUsed: formData.materialsUsed,
        equipmentUsed: formData.equipmentUsed,
        subcontractorsInvolved: formData.subcontractorsInvolved
      };

      this.workLogService.updateWorkLog(this.selectedWorkLog.id, workLog)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            this.showEditDialog = false;
            this.showNotification('Work log updated successfully', 'success');
            this.loadWorkLogs();
            this.loadWorkLogStats();
          },
          error: (error) => {
            console.error('Error updating work log:', error);
            this.showNotification('Failed to update work log', 'error');
          }
        });
    }
  }

  // Delete work log
  deleteWorkLog(workLog: WorkLog): void {
    if (confirm('Are you sure you want to delete this work log?')) {
      this.workLogService.deleteWorkLog(workLog.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            this.showNotification('Work log deleted successfully', 'success');
            this.loadWorkLogs();
            this.loadWorkLogStats();
          },
          error: (error) => {
            console.error('Error deleting work log:', error);
            this.showNotification('Failed to delete work log', 'error');
          }
        });
    }
  }

  // Open view dialog
  openViewDialog(workLog: WorkLog): void {
    this.loadWorkLog(workLog.id);
    this.showViewDialog = true;
  }

  // File upload handling
  onFileSelected(event: any): void {
    const files = event.target.files;
    if (files) {
      this.selectedFiles = Array.from(files);
      this.imagePreviewUrls = [];
      
      this.selectedFiles.forEach(file => {
        if (file.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onload = (e: any) => {
            this.imagePreviewUrls.push(e.target.result);
          };
          reader.readAsDataURL(file);
        }
      });
    }
  }

  // Upload images
  uploadImages(workLogId: number): void {
    if (this.selectedFiles.length === 0) {
      this.showNotification('Please select files to upload', 'error');
      return;
    }

    this.uploadingImages = true;
    const imageGeo: WorkLogImageGeo[] = this.selectedFiles.map(() => ({}));
    this.workLogService.uploadImages(workLogId, this.selectedFiles, { imageGeo })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.uploadingImages = false;
          this.selectedFiles = [];
          this.imagePreviewUrls = [];
          this.showNotification('Images uploaded successfully', 'success');
          this.loadWorkLog(workLogId);
        },
        error: (error) => {
          console.error('Error uploading images:', error);
          this.uploadingImages = false;
          this.showNotification('Failed to upload images', 'error');
        }
      });
  }

  // Add comment
  addComment(workLogId: number): void {
    if (this.commentForm.valid) {
      const comment = this.commentForm.get('comment')?.value;
      
      this.workLogService.addComment(workLogId, comment)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            this.commentForm.reset();
            this.showNotification('Comment added successfully', 'success');
            this.loadWorkLog(workLogId);
          },
          error: (error) => {
            console.error('Error adding comment:', error);
            this.showNotification('Failed to add comment', 'error');
          }
        });
    }
  }

  // Pagination
  onPageChange(event: any): void {
    this.currentPage = event.pageIndex + 1;
    this.loadWorkLogs();
  }

  // Filter changes
  onFilterChange(): void {
    this.currentPage = 1;
    this.loadWorkLogs();
    this.loadWorkLogStats();
  }

  onSearch(): void {
    this.currentPage = 1;
    this.loadWorkLogs();
  }

  // Utility methods
  getStatusClass(status: string): string {
    return this.workLogService.getStatusClass(status);
  }

  getStatusIcon(status: string): string {
    return this.workLogService.getStatusIcon(status);
  }

  getStatusText(status: string): string {
    return this.workLogService.getStatusText(status);
  }

  getPriorityClass(priority: string): string {
    return this.workLogService.getPriorityClass(priority);
  }

  getPriorityIcon(priority: string): string {
    return this.workLogService.getPriorityIcon(priority);
  }

  getPriorityText(priority: string): string {
    return this.workLogService.getPriorityText(priority);
  }

  formatDate(dateString: string): string {
    return this.workLogService.formatDate(dateString);
  }

  formatDateTime(dateString: string): string {
    return this.workLogService.formatDateTime(dateString);
  }

  imageUrl(path: string): string {
    return resolveWorkLogImageUrl(path);
  }

  formatFileSize(bytes: number): string {
    return this.workLogService.formatFileSize(bytes);
  }

  getFileIcon(fileType: string): string {
    return this.workLogService.getFileIcon(fileType);
  }

  // Form array helpers for tags
  get tagsArray(): FormArray {
    return this.workLogForm.get('tags') as FormArray;
  }

  addTag(): void {
    this.tagsArray.push(this.formBuilder.control(''));
  }

  removeTag(index: number): void {
    this.tagsArray.removeAt(index);
  }

  // Notifications
  private showNotification(message: string, type: 'success' | 'error'): void {
    this.snackBar.open(message, 'Close', {
      duration: 5000,
      horizontalPosition: 'end',
      verticalPosition: 'top',
      panelClass: type === 'success' ? ['success-snackbar'] : ['error-snackbar']
    });
  }

  // Close dialogs
  closeCreateDialog(): void {
    this.showCreateDialog = false;
    this.createAttachments = [];
    this.pendingGeo = null;
  }

  closeEditDialog(): void {
    this.showEditDialog = false;
    this.selectedWorkLog = null;
  }

  closeViewDialog(): void {
    this.showViewDialog = false;
    this.selectedWorkLog = null;
  }

  closeImageDialog(): void {
    this.showImageDialog = false;
  }

  // Clear file selection
  clearFileSelection(): void {
    this.selectedFiles = [];
    this.imagePreviewUrls = [];
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }
  getProgressClass(progress: number): string {
    if (progress >= 100) return 'progress-complete';
    if (progress >= 75) return 'progress-high';
    if (progress >= 50) return 'progress-medium';
    return 'progress-low';
  }

  private finishCreateSuccess(): void {
    this.showCreateDialog = false;
    this.createAttachments = [];
    this.pendingGeo = null;
    this.showNotification('Work log created successfully', 'success');
    this.loadWorkLogs();
    this.loadWorkLogStats();
  }

  private getCurrentPosition(): Promise<GeolocationPosition | null> {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve(pos),
        () => resolve(null),
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    });
  }
}

